// Component_mealPlan_details/Drag_and_Drop_funtion/Usemealoptiondragdrop.ts
// FINAL VERSION with proper event handling for showing placeholders

import { useState, useCallback } from 'react';
import { getDuplicateMealOptionDetails } from '../../../../../../services/mealPlans';
import { duplicateGlobalTemplateMealOptions } from '../../../../../../services/globalTemplates';
import { duplicateAdhocMealPlanOption } from '../../../../../../services/adhocMealPlans';

interface MealOption {
  id: string;
  name: string;
  mealTimingId: string;
  dishes: any[];
  instructions?: string;
  totals: {
    cal: number;
    carb: number;
    fat: number;
    protein: number;
    gl: number;
  };
  isNewlyCreated?: boolean;
  optionNumber?: number;
}

interface MealTiming {
  id: string;
  mealType: string;
  time: { hour: string; minute: string; period: string };
  fk_user_meal: number;
}

interface UseMealOptionDragDropProps {
  items: MealOption[];
  onReorder: (newItems: MealOption[]) => void;
  isDisabled?: boolean;
  mealPlanId?: string;
  isGlobalTemplateMode?: boolean;
  isAdhocMealMode?: boolean;
  mealTimings?: MealTiming[];
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
  onRefetch?: () => Promise<void>;
}

interface DragState {
  draggedItemId: string | null;
  draggedFromMealId: string | null;
  hoveredMealId: string | null;
  hoveredPosition: 'before' | 'after' | null;
  hoveredItemId: string | null;
}

export const useMealOptionDragDrop = ({
  items,
  onReorder,
  isDisabled = false,
  mealPlanId,
  isGlobalTemplateMode = false,
  isAdhocMealMode = false,
  mealTimings = [],
  onError,
  onSuccess,
  onRefetch,
}: UseMealOptionDragDropProps) => {
  const [dragState, setDragState] = useState<DragState>({
    draggedItemId: null,
    draggedFromMealId: null,
    hoveredMealId: null,
    hoveredPosition: null,
    hoveredItemId: null,
  });

  const [isDragging, setIsDragging] = useState(false);

  const moveOptionToMeal = useCallback(async (
    sourceOptionId: string,
    targetMealTimingId: string,
    optionName: string,
  ): Promise<{ success: boolean; newOptionId?: string; error?: string }> => {
    if (!mealPlanId) {
      return { success: false, error: 'Missing meal plan ID' };
    }

    try {
      let body: Record<string, any>;
      let result: any;

      if (isGlobalTemplateMode) {
        body = {
          template_id: parseInt(mealPlanId),
          source_option_id: parseInt(sourceOptionId),
          new_option_name: optionName,
          target_meal_id: parseInt(targetMealTimingId),
        };
        console.log('🌐 Move Option API Request (template):', body);
        result = await duplicateGlobalTemplateMealOptions(body);
      } else if (isAdhocMealMode) {
        // For adhoc, mealTiming.id and mealTiming.fk_user_meal both hold the
        // AdhocMealDetails.meal_id (see fetchAdhocMealPlanDetails mapping),
        // so parseInt(targetMealTimingId) is what the API expects.
        body = {
          adhoc_plan_id: parseInt(mealPlanId),
          source_option_id: parseInt(sourceOptionId),
          target_meal_id: parseInt(targetMealTimingId),
          new_option_name: optionName,
        };
        console.log('🌐 Move Option API Request (adhoc):', body);
        result = await duplicateAdhocMealPlanOption(body);
      } else {
        const targetMealTiming = mealTimings.find((mt) => mt.id === targetMealTimingId);
        if (!targetMealTiming) {
          return { success: false, error: 'Target meal timing not found' };
        }
        body = {
          meal_plan_id: parseInt(mealPlanId),
          source_option_id: parseInt(sourceOptionId),
          target_meal_id: targetMealTiming.fk_user_meal,
          new_option_name: optionName,
        };
        console.log('🌐 Move Option API Request:', body);
        result = await getDuplicateMealOptionDetails(body);
      }

      console.log('✅ API Success:', result);

      if (result.success === '1') {
        return {
          success: true,
          newOptionId: result.data.option_id.toString(),
        };
      } else {
        return {
          success: false,
          error: result.message || 'Failed to move option',
        };
      }
    } catch (err) {
      console.error('❌ API Error:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'An error occurred',
      };
    }
  }, [mealPlanId, isGlobalTemplateMode, isAdhocMealMode, mealTimings]);

  const handleDragStart = useCallback(
    (itemId: string, mealTimingId: string) => {
      if (isDisabled) return {};

      return {
        draggable: true,
        onDragStart: (e: React.DragEvent) => {
          console.log('🚀 Drag Start:', { itemId, mealTimingId });
          
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', itemId);

          setDragState({
            draggedItemId: itemId,
            draggedFromMealId: mealTimingId,
            hoveredMealId: null,
            hoveredPosition: null,
            hoveredItemId: null,
          });
          setIsDragging(true);

          setTimeout(() => {
            const containers = document.querySelectorAll('.meal-options-container');
            containers.forEach((c) => c.classList.add('is-dragging'));
          }, 0);
        },
        onDragEnd: (e: React.DragEvent) => {
          console.log('🏁 Drag End');
          
          const containers = document.querySelectorAll('.meal-options-container');
          containers.forEach((c) => c.classList.remove('is-dragging'));
          
          setIsDragging(false);
          
          setTimeout(() => {
            setDragState({
              draggedItemId: null,
              draggedFromMealId: null,
              hoveredMealId: null,
              hoveredPosition: null,
              hoveredItemId: null,
            });
          }, 100);
        },
      };
    },
    [isDisabled],
  );

  const handleDrop = useCallback(
    async (
      targetMealTimingId: string,
      beforeItemId: string | null = null,
      position: 'top' | 'bottom' | null = null,
    ) => {
      console.log('💧 Drop Handler Called:', {
        targetMealTimingId,
        beforeItemId,
        position,
        dragState,
      });

      if (!dragState.draggedItemId || !dragState.draggedFromMealId) {
        console.warn('⚠️ No drag state - ignoring drop');
        return;
      }

      const draggedItem = items.find((item) => item.id === dragState.draggedItemId);
      if (!draggedItem) {
        console.error('❌ Dragged item not found');
        return;
      }

      const sourceMealId = dragState.draggedFromMealId;
      const isMovingToNewMeal = sourceMealId !== targetMealTimingId;

      console.log('📊 Drop Analysis:', {
        draggedItem: draggedItem.name,
        isMovingToNewMeal,
        from: sourceMealId,
        to: targetMealTimingId,
      });

      try {
        if (isMovingToNewMeal && mealPlanId) {
          // Cross-meal move - call API
          console.log('🔄 Moving to different meal - calling API');

          const result = await moveOptionToMeal(
            draggedItem.id,
            targetMealTimingId,
            draggedItem.name,
          );

          if (!result.success) {
            console.error('❌ Move failed:', result.error);
            if (onError) onError(result.error || 'Failed to move option');
            return;
          }

          console.log('✅ Move succeeded, updating UI');

          // Adhoc endpoint semantically moves the option (returns action:"update"
          // and reuses the same option_id) — hide the source instantly.
          // Meal-plan / template endpoints copy — keep source visible until refetch.
          const hideSourceInstantly = isAdhocMealMode;

          const targetMealItems = items.filter(
            (item) =>
              item.mealTimingId === targetMealTimingId &&
              !(hideSourceInstantly && item.id === draggedItem.id),
          );

          // Create new item with new ID in target meal
          const newItem: MealOption = {
            ...draggedItem,
            id: result.newOptionId!,
            mealTimingId: targetMealTimingId,
            isNewlyCreated: true,
          };

          // Calculate insert position
          let insertIndex = targetMealItems.length;
          if (beforeItemId && position) {
            const beforeIndex = targetMealItems.findIndex((item) => item.id === beforeItemId);
            if (beforeIndex !== -1) {
              insertIndex = position === 'top' ? beforeIndex : beforeIndex + 1;
            }
          }

          const updatedTargetMealItems = [...targetMealItems];
          updatedTargetMealItems.splice(insertIndex, 0, newItem);

          const finalItems = [
            ...items.filter(
              (item) =>
                item.mealTimingId !== targetMealTimingId &&
                !(hideSourceInstantly && item.id === draggedItem.id),
            ),
            ...updatedTargetMealItems,
          ];

          onReorder(finalItems);

          if (onSuccess) {
            onSuccess(
              hideSourceInstantly
                ? `Moved "${draggedItem.name}" to new meal timing`
                : `Copied "${draggedItem.name}" to new meal timing`,
            );
          }

          // Refetch after delay
          if (onRefetch) {
            setTimeout(() => {
              console.log('🔄 Refetching data');
              onRefetch();
            }, 1000);
          }
        } else {
          // Same meal - local reorder
          console.log('🔄 Reordering within same meal');

          const itemsInMeal = items.filter((item) => item.mealTimingId === targetMealTimingId);
          const otherItems = items.filter((item) => item.mealTimingId !== targetMealTimingId);

          const draggedIndex = itemsInMeal.findIndex(
            (item) => item.id === dragState.draggedItemId,
          );

          if (draggedIndex === -1) {
            console.error('❌ Item not found in source meal');
            return;
          }

          const reorderedItems = [...itemsInMeal];
          const [removed] = reorderedItems.splice(draggedIndex, 1);

          let insertIndex = reorderedItems.length;
          if (beforeItemId && position) {
            const beforeIndex = reorderedItems.findIndex((item) => item.id === beforeItemId);
            if (beforeIndex !== -1) {
              insertIndex = position === 'top' ? beforeIndex : beforeIndex + 1;
            }
          }

          reorderedItems.splice(insertIndex, 0, removed);

          onReorder([...otherItems, ...reorderedItems]);
        }
      } catch (error) {
        console.error('❌ Drop error:', error);
        if (onError) {
          onError(error instanceof Error ? error.message : 'Drop failed');
        }
      }
    },
    [dragState, items, mealPlanId, isAdhocMealMode, moveOptionToMeal, onReorder, onError, onSuccess, onRefetch],
  );

  // Card hover detection - SIMPLIFIED approach
  const getCardDropZoneProps = useCallback(
    (itemId: string, mealTimingId: string) => {
      if (isDisabled) return {};

      return {
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          e.stopPropagation(); // Stop here so container doesn't interfere
          e.dataTransfer.dropEffect = 'move';
          
          // Calculate position based on mouse Y position
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          const midpoint = rect.top + rect.height / 2;
          const position = e.clientY < midpoint ? 'before' : 'after';
          
          // Always update state on dragOver (this is the key fix!)
          setDragState((prev) => ({
            ...prev,
            hoveredMealId: mealTimingId,
            hoveredItemId: itemId,
            hoveredPosition: position,
          }));
        },
      };
    },
    [isDisabled],
  );

  const getCardProps = useCallback(
    (itemId: string, mealTimingId: string) => {
      if (isDisabled) return {};
      return {
        ...handleDragStart(itemId, mealTimingId),
        ...getCardDropZoneProps(itemId, mealTimingId),
      };
    },
    [isDisabled, handleDragStart, getCardDropZoneProps],
  );

  const getItemClassName = useCallback(
    (itemId: string, mealTimingId: string) => {
      return dragState.draggedItemId === itemId ? 'dragging-item' : '';
    },
    [dragState.draggedItemId],
  );

  const getMealTimingDropZoneProps = useCallback(
    (mealTimingId: string) => {
      if (isDisabled) return {};

      return {
        onDragEnter: (e: React.DragEvent) => {
          e.preventDefault();
          // Don't stop propagation - let it reach cards
          
          // Only update meal ID, don't clear item hover state
          setDragState((prev) => ({
            ...prev,
            hoveredMealId: mealTimingId,
          }));
        },
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          // Don't stop propagation
          e.dataTransfer.dropEffect = 'move';
        },
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Only handle drop if we're not over a specific card
          const target = e.target as HTMLElement;
          const isOverCard = target.closest('[data-option-id]');
          
          if (!isOverCard) {
            console.log('💧 Drop on empty area of meal timing:', mealTimingId);
            handleDrop(mealTimingId, null, null);
          }
        },
      };
    },
    [isDisabled, handleDrop],
  );

  const shouldShowPlaceholder = useCallback(
    (
      mealTimingId: string,
      position: 'before' | 'after' | 'empty',
      itemId: string | null = null,
    ) => {
      if (!isDragging || !dragState.draggedItemId) {
        return false;
      }

      const isEmptyMeal = items.filter((item) => item.mealTimingId === mealTimingId).length === 0;

      // For empty meal timing
      if (position === 'empty') {
        return isEmptyMeal && dragState.hoveredMealId === mealTimingId;
      }

      // For placeholders around cards
      if (!itemId) return false;
      if (dragState.draggedItemId === itemId) return false;

      const show = (
        dragState.hoveredMealId === mealTimingId &&
        dragState.hoveredItemId === itemId &&
        dragState.hoveredPosition === position
      );

      // Debug logging
      if (show) {
        console.log('✅ Showing placeholder:', { mealTimingId, position, itemId });
      }

      return show;
    },
    [isDragging, dragState, items],
  );

  return {
    getCardProps,
    getItemClassName,
    getMealTimingDropZoneProps,
    shouldShowPlaceholder,
    handleDrop,
    isDragging,
    dragState, // Export for debugging
  };
};